import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setErr("Missing reset token. Please request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 12) {
      setErr("Password must be at least 12 characters.");
      return;
    }
    const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
    if (classes < 3) {
      setErr("Password must include at least 3 of: lowercase, uppercase, digit, symbol.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can sign in.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (e) {
      const m = getErrorMessage(e);
      setErr(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen ss-gradient-warm flex items-center justify-center px-4 py-12">
      <div className="ss-card w-full max-w-md p-8 sm:p-10">
        <Link to="/"><Logo size="lg" /></Link>
        <div className="mt-10">
          <div className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-3">Secure reset</div>
          <h1 className="font-heading text-3xl text-cocoa">Set a new password.</h1>
        </div>
        {!done ? (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <Label className="text-[10px] tracking-[0.3em] uppercase text-cocoa/50">New password</Label>
              <Input
                data-testid="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="mt-2 bg-ivory border-champagne text-cocoa rounded-md h-12 focus-visible:border-mauve focus-visible:ring-0"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.3em] uppercase text-cocoa/50">Confirm</Label>
              <Input
                data-testid="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                className="mt-2 bg-ivory border-champagne text-cocoa rounded-md h-12 focus-visible:border-mauve focus-visible:ring-0"
              />
            </div>
            {err && <div data-testid="reset-error" className="text-sm text-rose">{err}</div>}
            <Button
              type="submit"
              disabled={loading || !token}
              data-testid="reset-submit"
              className="ss-pill ss-pill-primary w-full h-12"
            >
              {loading ? "Updating…" : "Reset password"}
            </Button>
          </form>
        ) : (
          <div className="mt-8 text-sm text-cocoa/70" data-testid="reset-done">
            Password updated. Redirecting to sign in…
          </div>
        )}
        <p className="mt-8 text-sm text-cocoa/60">
          <Link to="/login" className="hover:text-cocoa" data-testid="reset-to-login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
